import { SlackWorkspaces } from "./slack-workspaces/SlackWorkspaces";
import { parseAsStringLiteral, useQueryState } from "nuqs";

import { TabbedPage, type PageTab } from "@/components/page-templates";
import { RequireScope } from "@/components/require-scope";
import { Heading } from "@/components/ui/Heading";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import { Text } from "@/components/ui/Text";
import { useOrganization, useSessionData } from "@/contexts/Auth";
import { useTelemetry } from "@/contexts/Telemetry";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useRBAC } from "@/hooks/useRBAC";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { openSafeExternalUrl } from "@/lib/safe-external-url";
import { cn } from "@/lib/utils";
import { useGenerateWorkOSAdminPortalLinkMutation } from "@gram/client/react-query/generateWorkOSAdminPortalLink.js";
import { useOnboardingStatus } from "@gram/client/react-query/onboardingStatus";
import { useProductFeatures } from "@gram/client/react-query/productFeatures.js";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ExternalLink, FolderSync, Globe, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

import { DirectoryRoleMappings } from "./identity-provider/DirectoryRoleMappings";
import { IdentityProviders } from "./identity-provider/IdentityProviders";
import { useEnterpriseIssuer } from "./identity-provider/useEnterpriseIssuer";
import {
  identityProvidersHref,
  IDENTITY_TABS,
  type IdentityPageTab,
} from "./identity-provider/tabs";

const UPSELL_COPY = "Contact our team to setup SSO and Directory Sync";

type IdentitySectionId = "domain_verification" | "sso" | "directory_sync";

type IdentityCardProps = {
  sectionId: IdentitySectionId;
  heading: string;
  description: React.ReactNode;
  providerIcon: React.ReactNode;
  providerTitle: string;
  providerSubtitle: string;
  learnMoreText: string;
  learnMoreHref: string;
  active?: boolean;
  activeLabel?: string;
  /** Dims the card and disables its control until a domain is verified. */
  blocked?: boolean;
  configureButton?: React.ReactNode;
  children?: React.ReactNode;
  /** Content below the card, outside its border, such as a table. */
  below?: React.ReactNode;
  /** Leaves out the provider card, for sections whose content replaces it. */
  hideCard?: boolean;
};

/**
 * Notifies our team that a non-entitled org wants SSO / Directory Sync. This
 * capture backs the "our team has been contacted" upsell toast, so it stays even
 * though the entitled-org configure buttons no longer emit tracking events.
 */
function useUpsellInterestCapture(sectionId: IdentitySectionId) {
  const telemetry = useTelemetry();
  const { session } = useSessionData();

  return () => {
    telemetry.capture("identity_provider_interest", {
      section: sectionId,
      action: "configure_clicked",
      email: session?.user.email ?? "",
      organization_id: session?.organization?.id ?? "",
      organization_name: session?.organization?.name ?? "",
      organization_slug: session?.organization?.slug ?? "",
    });
  };
}

function ConfigureButton({ sectionId }: { sectionId: IdentitySectionId }) {
  const captureInterest = useUpsellInterestCapture(sectionId);

  return (
    <SimpleTooltip tooltip={UPSELL_COPY}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          captureInterest();
          toast.success(
            "Our team has been contacted to enable SSO and Directory Sync",
          );
        }}
      >
        Configure
      </Button>
    </SimpleTooltip>
  );
}

/** Launches the WorkOS admin portal for the given intent. */
function WorkOSPortalButton({
  intent,
  errorFallback,
  label = "Configure",
  external = false,
  variant = "secondary",
}: {
  intent: "sso" | "dsync" | "domain_verification";
  errorFallback: string;
  label?: string;
  /** Adds an external-link icon, for spots where leaving Speakeasy is not obvious. */
  external?: boolean;
  /** Primary when opening the portal is the card's first-run action. */
  variant?: "primary" | "secondary";
}) {
  const generatePortalLink = useGenerateWorkOSAdminPortalLinkMutation({
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : errorFallback);
    },
  });

  const launchPortal = () => {
    generatePortalLink.mutate(
      {
        request: {
          generateWorkOSAdminPortalLinkRequestBody: {
            intent,
          },
        },
      },
      {
        onSuccess: (data) => {
          if (openSafeExternalUrl(data.url)) {
            toast.info("Continue setup in the WorkOS portal");
          } else {
            toast.error("Unable to open the WorkOS portal");
          }
        },
      },
    );
  };

  return (
    <RequireScope scope="org:admin" level="component">
      <Button
        variant={variant}
        size="sm"
        onClick={launchPortal}
        disabled={generatePortalLink.isPending}
      >
        {generatePortalLink.isPending && (
          <Button.LeftIcon>
            <Loader2 className="h-4 w-4 animate-spin" />
          </Button.LeftIcon>
        )}
        {label}
        {external && (
          <Button.RightIcon>
            <ExternalLink className="h-4 w-4" />
          </Button.RightIcon>
        )}
      </Button>
    </RequireScope>
  );
}

/**
 * Picks the SSO configure control: upsell when the feature is not entitled,
 * otherwise the WorkOS portal launcher, as the primary action until a
 * connection exists.
 */
function SSOConfigureControl({
  featureEnabled,
  active,
}: {
  featureEnabled: boolean;
  active: boolean;
}) {
  if (!featureEnabled) return <ConfigureButton sectionId="sso" />;
  if (active) {
    return (
      <WorkOSPortalButton
        intent="sso"
        errorFallback="Failed to start SSO setup"
      />
    );
  }
  return (
    <WorkOSPortalButton
      intent="sso"
      errorFallback="Failed to start SSO setup"
      variant="primary"
    />
  );
}

/**
 * Picks the Directory Sync card control: upsell, or the WorkOS portal to
 * connect a directory, as the card's primary action. A connected directory
 * has none on the card; its connection is managed from the secondary button
 * under the role mappings.
 */
function DirectorySyncConfigureControl({
  featureEnabled,
  active,
}: {
  featureEnabled: boolean;
  active: boolean;
}) {
  if (active) return null;
  if (!featureEnabled) return <ConfigureButton sectionId="directory_sync" />;
  return (
    <WorkOSPortalButton
      intent="dsync"
      errorFallback="Failed to start Directory Sync setup"
      variant="primary"
    />
  );
}

function IdentitySection({
  sectionId,
  heading,
  description,
  providerIcon,
  providerTitle,
  providerSubtitle,
  learnMoreText,
  learnMoreHref,
  active,
  activeLabel = "Connected",
  blocked = false,
  configureButton,
  children,
  below,
  hideCard = false,
}: IdentityCardProps) {
  let control = configureButton ?? <ConfigureButton sectionId={sectionId} />;
  // A blocked card replaces every control, including the upsell, so nothing
  // starts setup that WorkOS would reject without a verified domain.
  if (blocked) {
    control = (
      <Button variant="secondary" size="sm" disabled>
        Configure
      </Button>
    );
  }

  return (
    <section>
      <div className="flex flex-col">
        <Heading variant="h5" className="mb-1">
          {heading}
        </Heading>
        <Text as="div" muted small className="mb-4">
          {description}
        </Text>
        {blocked && (
          <Alert variant="warning" className="mb-4">
            <Text small className="text-inherit">
              Verify a domain above before setting up {heading}.
            </Text>
          </Alert>
        )}
        {!hideCard && (
          <div
            aria-disabled={blocked || undefined}
            className={cn(
              "border-border overflow-hidden border",
              blocked && "opacity-70",
            )}
          >
            <div className="flex items-center gap-4 p-4">
              <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                {providerIcon}
              </div>
              <div className="min-w-0 flex-1">
                <Text variant="body" className="font-medium">
                  {providerTitle}
                </Text>
                <Text muted small>
                  {providerSubtitle}
                </Text>
                {active && (
                  <Badge variant="success" className="mt-1.5">
                    <Badge.Text>{activeLabel}</Badge.Text>
                  </Badge>
                )}
              </div>
              {control}
            </div>
            {children}
          </div>
        )}
        {below}
        <a
          href={learnMoreHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground mt-2 ml-auto block text-sm underline underline-offset-4 transition-colors"
        >
          {learnMoreText}
        </a>
      </div>
    </section>
  );
}

export default function OrgIdentity(): JSX.Element {
  const [requestedTab] = useQueryState(
    "tab",
    parseAsStringLiteral(IDENTITY_TABS).withDefault("sso"),
  );
  // Without the rollout flag (or org:admin) the tab does not exist.
  const providerFlag = useFeatureFlag(FEATURE_FLAGS.oktaConnections);
  const { hasScope } = useRBAC();
  const issuer = useEnterpriseIssuer();
  const showExternalProviders = providerFlag.status === "enabled";
  // Speakeasy's own issuer is listed once the organization is offered it, and
  // whenever one exists or its status failed to load, so Disable and Retry stay
  // reachable.
  const showSpeakeasyProvider =
    issuer.isError ||
    (issuer.data !== undefined &&
      (issuer.data.state !== "unavailable" || !!issuer.data.issuer));
  const showIdentityProviders =
    (showExternalProviders || showSpeakeasyProvider) && hasScope("org:admin");

  const showSlack = hasScope("org:admin");
  let activeTab: IdentityPageTab = requestedTab;
  if (requestedTab === "identity-providers" && !showIdentityProviders)
    activeTab = "sso";
  if (requestedTab === "slack-workspaces" && !showSlack) activeTab = "sso";

  const tabs: PageTab[] = [
    { value: "sso", label: "Single sign-on", href: "?tab=sso" },
    ...(showSlack
      ? [
          {
            value: "slack-workspaces",
            label: "Slack workspaces",
            href: "?tab=slack-workspaces",
            stage: "preview" as const,
          },
        ]
      : []),
    ...(showIdentityProviders
      ? [
          {
            value: "identity-providers",
            label: "Identity providers",
            href: identityProvidersHref(),
            stage: "preview" as const,
          },
        ]
      : []),
  ];

  return (
    <TabbedPage
      scope={["org:read", "org:admin"]}
      title="IDP and SSO"
      description="Manage employee sign-in, directory sync, and agent access through your identity providers."
      activeTab={activeTab}
      tabs={tabs}
    >
      {activeTab === "sso" && <SingleSignOnTab />}
      {activeTab === "identity-providers" && (
        <IdentityProviders
          showExternal={showExternalProviders}
          showSpeakeasy={showSpeakeasyProvider}
        />
      )}
      {activeTab === "slack-workspaces" && <SlackWorkspaces />}
    </TabbedPage>
  );
}

function SingleSignOnTab(): JSX.Element {
  const organization = useOrganization();
  const { data: features } = useProductFeatures({
    organizationId: organization.id,
  });
  const { data: onboardingStatus } = useOnboardingStatus(undefined, undefined, {
    throwOnError: false,
  });

  const ssoFeatureEnabled = features?.ssoEnabled ?? false;
  const scimFeatureEnabled = features?.scimEnabled ?? false;
  const ssoActive = organization.ssoEnabled === true;
  const scimActive = organization.scimEnabled === true;
  // Only org admins get the role mappings, which replace the SCIM card; every
  // one else keeps the card so the section is never empty.
  const { hasScope } = useRBAC();
  const showRoleMappings = scimActive && hasScope("org:admin");
  // Active SSO proves a domain was verified, even for orgs set up before
  // verified domains were tracked. The server completes the setup task the
  // same way.
  const domainVerified =
    !!onboardingStatus?.domainVerified || !!onboardingStatus?.ssoConfigured;
  const verifiedDomains = onboardingStatus?.verifiedDomains ?? [];
  // WorkOS needs a verified domain before either connection can be set up.
  // Only a status response can say the domain is unverified: while loading or
  // after an error nothing is blocked, and WorkOS still enforces the rule.
  const domainUnverified = onboardingStatus !== undefined && !domainVerified;
  const ssoBlocked = !ssoActive && domainUnverified;
  const scimBlocked = !scimActive && domainUnverified;

  let domainSubtitle = "Add a DNS record to verify your domain.";
  if (verifiedDomains.length > 0)
    domainSubtitle = "SSO applies to users on these domains.";
  else if (domainVerified) domainSubtitle = "Your domain is verified.";

  let ssoSubtitle = "Choose an identity provider to get started.";
  if (ssoActive) ssoSubtitle = "Your identity provider is connected.";
  else if (ssoBlocked) ssoSubtitle = "Verify a domain first.";

  let scimSubtitle = "Choose an identity provider to get started.";
  if (scimActive) scimSubtitle = "Your directory provider is connected.";
  else if (scimBlocked) scimSubtitle = "Verify a domain first.";

  return (
    <div className="flex max-w-4xl flex-col gap-4">
      <IdentitySection
        sectionId="domain_verification"
        heading="Domain verification"
        description="Prove your organization owns its email domain. Single Sign-On needs a verified domain."
        providerIcon={<Globe className="text-muted-foreground h-5 w-5" />}
        providerTitle="Domain"
        providerSubtitle={domainSubtitle}
        learnMoreText="Learn more about domain verification"
        learnMoreHref="https://www.speakeasy.com/docs/ai-control-plane/org-admin/identity"
        active={domainVerified}
        activeLabel="Verified"
        configureButton={
          <WorkOSPortalButton
            intent="domain_verification"
            errorFallback="Failed to start domain verification"
            label={domainVerified ? "Manage" : "Verify domain"}
          />
        }
      >
        {verifiedDomains.length > 0 && (
          <ul
            aria-label="Verified domains"
            className="border-border flex flex-wrap gap-2 border-t p-4"
          >
            {verifiedDomains.map((domain) => (
              <li key={domain}>
                <Badge variant="neutral" background>
                  <Badge.Text>{domain}</Badge.Text>
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </IdentitySection>

      <IdentitySection
        sectionId="sso"
        heading="Single Sign-On"
        description="Set up Single Sign-On (SSO) to allow your team to sign in to Speakeasy with your identity provider."
        providerIcon={<Lock className="text-muted-foreground h-5 w-5" />}
        providerTitle="SSO"
        providerSubtitle={ssoSubtitle}
        learnMoreText="Learn more about SSO"
        learnMoreHref="https://www.speakeasy.com/docs/ai-control-plane/org-admin/identity"
        active={ssoActive}
        blocked={ssoBlocked}
        configureButton={
          <SSOConfigureControl
            featureEnabled={ssoFeatureEnabled}
            active={ssoActive}
          />
        }
      />

      <IdentitySection
        sectionId="directory_sync"
        heading="Directory Sync"
        description={
          <>
            Sync members and roles directly from your identity provider:
            <ul className="mt-1.5 list-disc space-y-0.5 pl-5">
              <li>Members are provisioned automatically from your directory</li>
              <li>
                Roles are assigned from the group and attribute mappings below
              </li>
              <li>Members can&apos;t be invited manually</li>
              <li>Roles can&apos;t be assigned to members manually</li>
            </ul>
          </>
        }
        providerIcon={<FolderSync className="text-muted-foreground h-5 w-5" />}
        providerTitle="SCIM"
        providerSubtitle={scimSubtitle}
        learnMoreText="Learn more about SCIM Directory Sync"
        learnMoreHref="https://www.speakeasy.com/docs/ai-control-plane/org-admin/identity"
        active={scimActive}
        blocked={scimBlocked}
        configureButton={
          <DirectorySyncConfigureControl
            featureEnabled={scimFeatureEnabled}
            active={scimActive}
          />
        }
        // Once a directory is connected, an admin's section is the role
        // mappings; the connection is managed from the button below them.
        hideCard={showRoleMappings}
        below={
          scimActive && (
            // "section" renders nothing for non-admins, so the admin-only
            // listing is never requested.
            <RequireScope scope="org:admin" level="section">
              <DirectoryRoleMappings
                footerAction={
                  <WorkOSPortalButton
                    intent="dsync"
                    errorFallback="Failed to open the directory connection"
                    label="Manage connection"
                    external
                  />
                }
              />
            </RequireScope>
          )
        }
      />
    </div>
  );
}
